import { fetchWithAuth } from '../AuthService';
import { DATA_SERVICE_BASE_URL } from '../ApiService';

/**
 * Document Notes API service
 * Handles note creation, retrieval, updating, and deletion
 */
export const DocumentNotesApi = {
  /**
   * Create a new note
   * @param {Object} noteData - Note data
   * @param {string} noteData.title - Note title
   * @param {string} noteData.description - Note description (optional)
   * @param {string} noteData.content - Markdown content
   * @param {number} noteData.endeavorId - Course/endeavor ID (optional)
   * @param {number} noteData.parentDocumentId - Parent document ID (optional)
   * @param {number[]} noteData.statusIds - Status IDs (optional)
   */
  createNote: async (noteData) => {
    console.log('[DocumentNotesApi] Creating note with data:', noteData);
    const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/documents/notes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(noteData)
    });
    const data = await response.json();
    console.log('[DocumentNotesApi] Create note response data:', data);
    return data;
  },

  /**
   * Get note content (markdown)
   * @param {number} documentId - Document/note ID
   * @returns {Promise<{content: string, hash: string}>}
   */
  getNoteContent: async (documentId) => {
    const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/documents/${documentId}/content`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch note content: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Update note content
   * @param {number} documentId - Document/note ID
   * @param {string} content - Updated markdown content
   */
  updateNoteContent: async (documentId, content) => {
    const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/documents/${documentId}/content`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content })
    });
    if (!response.ok) {
      throw new Error(`Failed to update note content: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Get all notes for a specific document (by parentDocumentId)
   * @param {number} parentDocumentId - Parent document ID
   * @param {number} userId - User ID
   */
  getNotesByDocument: async (parentDocumentId, userId) => {
    console.log('[DocumentNotesApi] Getting notes for parent document:', parentDocumentId, 'user:', userId);
    const response = await fetchWithAuth(
      `${DATA_SERVICE_BASE_URL}/api/users/${userId}/documents?documentTypeId=6&page=0&size=100&sort=createdAt,desc`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      }
    );
    const data = await response.json();
    console.log('[DocumentNotesApi] Raw data from getNotesByDocument:', data);
    // Filter by parentDocumentId on client side (can be optimized with backend endpoint later)
    if (data.content) {
      console.log('[DocumentNotesApi] First note full object:', JSON.stringify(data.content[0], null, 2));
      console.log('[DocumentNotesApi] All notes with parentDocumentId:', data.content.map(doc => ({ id: doc.id, name: doc.name, parentDocumentId: doc.parentDocumentId })));
      console.log('[DocumentNotesApi] Looking for parentDocumentId:', parentDocumentId, 'type:', typeof parentDocumentId);
      const filtered = data.content.filter(doc => {
        const matches = doc.parentDocumentId === parentDocumentId;
        console.log('[DocumentNotesApi] Note', doc.id, 'parentDocumentId:', doc.parentDocumentId, 'type:', typeof doc.parentDocumentId, 'matches:', matches);
        return matches;
      });
      console.log('[DocumentNotesApi] Filtered notes:', filtered, 'from total:', data.content.length);
      return {
        ...data,
        content: filtered
      };
    }
    return data;
  },

  /**
   * Get all notes for a specific course/endeavor
   * @param {number} endeavorId - Endeavor/course ID
   * @param {number} userId - User ID
   */
  getNotesByEndeavor: async (endeavorId, userId) => {
    const response = await fetchWithAuth(
      `${DATA_SERVICE_BASE_URL}/api/users/${userId}/documents?documentTypeId=6&endeavorId=${endeavorId}&page=0&size=100&sort=createdAt,desc`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      }
    );
    if (!response.ok) {
      throw new Error(`Failed to fetch notes: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Finalize a note (save-close).
   *
   * Persists content like updateNoteContent, but the server returns
   * `{ contentChanged, needsReindex }`. When `needsReindex` is true the
   * caller should follow up with `triggerReindex(documentId)` so that
   * AI-Service regenerates the markdown summary embedding.
   *
   * @param {number} documentId
   * @param {string} content
   * @returns {Promise<{success:boolean, newHash:string, contentChanged:boolean, needsReindex:boolean, gcsKey?:string}>}
   */
  finalizeNote: async (documentId, content) => {
    const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/documents/notes/${documentId}/finalize`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content })
    });
    if (!response.ok) {
      throw new Error(`Failed to finalize note: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Ask AI-Service to (re-)ingest the document. For markdown-class notes this
   * regenerates the single summary embedding (chunk_index=-1). Best-effort —
   * callers should not block UX on the result.
   * @param {number} documentId
   */
  triggerReindex: async (documentId) => {
    const response = await fetchWithAuth(
      `${DATA_SERVICE_BASE_URL}/api/proxy/ai-service/api/documents/${documentId}/process/chunks`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force: true })
      }
    );
    if (!response.ok) {
      throw new Error(`Failed to trigger reindex: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Rename a note
   * @param {number} noteId - Note/document ID
   * @param {string} newName - New name for the note
   */
  renameNote: async (noteId, newName) => {
    const response = await fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/documents/${noteId}/name`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newName })
    });
    if (!response.ok) {
      throw new Error(`Failed to rename note: ${response.status}`);
    }
    return response.json();
  }
};
